// Copyright (c) 2026 TRIVONT IT Private Limited. All rights reserved.
import { useState } from 'react';
import {
  BookOpen, ChevronRight, AlertCircle, Settings, GitBranch,
  Users, Clock, Shield, Bell, Workflow, BarChart3, Mail,
  CheckCircle, XCircle, RefreshCw, ArrowRight, Paperclip,
  Search, Lock, UserCog, Cog, Calendar, Database, ArrowUpFromLine,
  Info, HelpCircle, Star, Zap
} from 'lucide-react';

const SECTIONS = [
  { id: 'overview',       label: 'Overview',              icon: BookOpen },
  { id: 'roles',          label: 'Roles & Permissions',   icon: Users },
  { id: 'tickets',        label: 'Ticket Types',          icon: AlertCircle },
  { id: 'lifecycle',      label: 'Ticket Lifecycle',      icon: Workflow },
  { id: 'sla',            label: 'SLA Management',        icon: Clock },
  { id: 'approvals',      label: 'Approval Workflow',     icon: CheckCircle },
  { id: 'emails',         label: 'Email Notifications',   icon: Mail },
  { id: 'escalation',     label: 'Escalation',            icon: ArrowUpFromLine },
  { id: 'applications',   label: 'Application Config',    icon: Settings },
  { id: 'users',          label: 'User Management',       icon: UserCog },
  { id: 'mfa',            label: 'MFA & Security',        icon: Lock },
  { id: 'attachments',    label: 'Attachments',           icon: Paperclip },
  { id: 'shift',          label: 'Shift Calendar',        icon: Calendar },
  { id: 'cmdb',           label: 'CMDB & Knowledge Base', icon: Database },
  { id: 'superadmin',     label: 'Superadmin Portal',     icon: Shield },
];

function Section({ id, title, icon: Icon, children }) {
  return (
    <div id={id} className="mb-10 scroll-mt-20">
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-200 dark:border-slate-700">
        <div className="w-8 h-8 bg-sky-100 dark:bg-sky-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-sky-600 dark:text-sky-400" />
        </div>
        <h2 className="text-xl font-bold text-slate-800 dark:text-white">{title}</h2>
      </div>
      <div className="text-slate-600 dark:text-slate-300 space-y-4 text-sm leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function Tip({ children }) {
  return (
    <div className="flex gap-2 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-xl p-3">
      <Info className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" />
      <span className="text-sky-800 dark:text-sky-300 text-xs">{children}</span>
    </div>
  );
}

function Table({ headers, rows }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 my-3">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800">
          <tr>{headers.map(h => <th key={h} className="px-4 py-2.5 text-left font-semibold text-slate-700 dark:text-slate-300 text-xs">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
              {row.map((cell, j) => <td key={j} className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Step({ num, title, children }) {
  return (
    <div className="flex gap-3">
      <div className="w-6 h-6 rounded-full bg-sky-500 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-bold">{num}</div>
      <div>
        <p className="font-semibold text-slate-800 dark:text-white">{title}</p>
        {children && <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">{children}</p>}
      </div>
    </div>
  );
}

function Flow({ steps }) {
  return (
    <div className="flex flex-wrap items-center gap-1 my-3">
      {steps.map((s, i) => (
        <div key={i} className="flex items-center gap-1">
          <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-300">{s}</span>
          {i < steps.length - 1 && <ArrowRight className="w-3 h-3 text-slate-400 flex-shrink-0" />}
        </div>
      ))}
    </div>
  );
}

export default function HelpPage() {
  const [active, setActive] = useState('overview');

  const scrollTo = (id) => {
    setActive(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="flex h-full min-h-screen bg-white dark:bg-slate-900">
      {/* Sticky TOC sidebar */}
      <aside className="hidden lg:flex flex-col w-56 flex-shrink-0 border-r border-slate-200 dark:border-slate-700 sticky top-0 h-screen overflow-y-auto">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-sky-500" />
            <span className="font-bold text-slate-800 dark:text-white text-sm">Help Center</span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">End-to-end guide</p>
        </div>
        <nav className="p-2 space-y-0.5">
          {SECTIONS.map(s => (
            <button key={s.id} onClick={() => scrollTo(s.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-left transition-colors ${active === s.id ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 font-semibold' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
              <s.icon className="w-3.5 h-3.5 flex-shrink-0" />
              {s.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-6 py-8 max-w-4xl">

        {/* Header */}
        <div className="mb-8 p-6 bg-gradient-to-br from-sky-500 to-indigo-600 rounded-2xl text-white">
          <div className="flex items-center gap-3 mb-2">
            <HelpCircle className="w-8 h-8" />
            <h1 className="text-2xl font-bold">TRIVONT SupportOne — Help Center</h1>
          </div>
          <p className="text-sky-100 text-sm">Complete end-to-end guide for all roles — from raising a ticket to managing SLAs, approvals, and tenant administration.</p>
        </div>

        {/* ── OVERVIEW ──────────────────────────────────────────────────────── */}
        <Section id="overview" title="Overview" icon={BookOpen}>
          <p>
            <strong>TRIVONT SupportOne</strong> is a multi-tenant IT Service Management (ITSM) platform built for organizations to manage <strong>Incidents</strong>, <strong>Service Requests</strong>, and <strong>Change Requests</strong> with built-in SLA tracking, multi-level approval chains, and automated email notifications.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-4">
            {[
              { icon: AlertCircle, color: 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400', title: 'Incident', desc: 'Something is broken. Report and track unplanned disruptions.' },
              { icon: Settings, color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400', title: 'Service Request', desc: 'Request access, hardware, software or other IT services.' },
              { icon: GitBranch, color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400', title: 'Change Request', desc: 'Propose and track planned changes to infrastructure or systems.' },
            ].map(c => (
              <div key={c.title} className={`p-3 rounded-xl border border-slate-200 dark:border-slate-700`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${c.color}`}>
                  <c.icon className="w-4 h-4" />
                </div>
                <p className="font-semibold text-slate-800 dark:text-white text-sm">{c.title}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{c.desc}</p>
              </div>
            ))}
          </div>

          <p><strong>Key concepts:</strong></p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Every ticket belongs to an <strong>Application</strong> (e.g. "Payment Gateway", "CRM System")</li>
            <li>Each application has its own approval chain, SLA policies, team assignments, and escalation rules</li>
            <li>SLAs track <strong>Response Time</strong> and <strong>Resolution Time</strong> per ticket priority</li>
            <li>Emails are sent automatically at every lifecycle event — creation, assignment, status change, approval, SLA breach</li>
          </ul>
        </Section>

        {/* ── ROLES ──────────────────────────────────────────────────────────── */}
        <Section id="roles" title="Roles & Permissions" icon={Users}>
          <p>The system has <strong>5 roles</strong>. Each user is assigned exactly one role by an Admin.</p>

          <Table
            headers={['Role', 'Who is this?', 'Key Permissions']}
            rows={[
              ['Admin', 'System administrator', 'Full access — manage users, roles, applications, system config, audit logs'],
              ['IT Manager', 'Team lead / Manager', 'Manage applications, SLA policies, teams, escalation, assign tickets, view all reports'],
              ['Approver', 'L1/L2/L3 approver', 'Approve/reject SR and CR tickets, delegate approvals, view assigned tickets'],
              ['IT Support', 'Technical support staff', 'Handle assigned tickets, update status, add work notes, view all tickets'],
              ['End User', 'Business user / Requester', 'Raise tickets, track their own tickets, withdraw requests, add comments'],
            ]}
          />

          <Tip>End Users can ONLY see tickets they raised. They cannot see work notes (internal notes) or other users' tickets.</Tip>

          <p className="mt-3"><strong>Reassignment:</strong> Only <strong>Admin</strong>, <strong>IT Manager</strong>, or the application's designated <strong>Team Lead / App Manager / App Head</strong> can reassign tickets. IT Support cannot reassign.</p>
        </Section>

        {/* ── TICKET TYPES ───────────────────────────────────────────────────── */}
        <Section id="tickets" title="Ticket Types" icon={AlertCircle}>
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-rose-500" /> Incident
              </p>
              <p>An unplanned disruption or degradation of service. Incidents do NOT require approvals — they are assigned directly and resolved.</p>
              <p className="text-xs text-slate-500 mt-1"><strong>Fields:</strong> Priority (P1–P4), Impact, Urgency, Category, Application</p>
              <Flow steps={['New', 'Assigned', 'In Progress', 'Resolved', 'Closed']} />
            </div>

            <div>
              <p className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                <Settings className="w-4 h-4 text-blue-500" /> Service Request (SR)
              </p>
              <p>A formal request for something new — access, hardware, software, or other IT services. SR requires approvals before assignment.</p>
              <p className="text-xs text-slate-500 mt-1"><strong>Fields:</strong> Urgency Level (ASAP/Standard/etc.), Category, Application, Attachment</p>
              <Flow steps={['Pending Approval', 'L1 Review', 'L2 Review', 'Approved', 'Assigned', 'In Progress', 'Resolved', 'Closed']} />
            </div>

            <div>
              <p className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                <GitBranch className="w-4 h-4 text-purple-500" /> Change Request (CR)
              </p>
              <p>A planned change to infrastructure or systems. CRs require the most rigorous approval chain and include risk assessment, scheduling, and rollback planning.</p>
              <p className="text-xs text-slate-500 mt-1"><strong>Fields:</strong> Change Type (Standard/Emergency/Normal), Risk Level, Scheduled Start/End, Rollback Plan</p>
              <Flow steps={['Draft', 'Pending Approval', 'Approved', 'Scheduled', 'In Progress', 'Resolved', 'Closed']} />
            </div>
          </div>

          <Tip>
            End users can raise Incidents and Service Requests. Only IT staff can raise Change Requests.
          </Tip>
        </Section>

        {/* ── LIFECYCLE ──────────────────────────────────────────────────────── */}
        <Section id="lifecycle" title="Ticket Lifecycle" icon={Workflow}>
          <p>All tickets follow a defined lifecycle. Here is the complete status flow:</p>

          <Table
            headers={['Status', 'Meaning', 'Who can set it']}
            rows={[
              ['New / Pending Approval', 'Ticket just created, awaiting action', 'Auto (system)'],
              ['Assigned', 'Assigned to an IT Support member', 'IT Manager / App Lead'],
              ['In Progress', 'Actively being worked on', 'IT Support'],
              ['Pending User', 'Waiting for reporter\'s response', 'IT Support'],
              ['Resolved', 'Solution provided, awaiting confirmation', 'IT Support'],
              ['Closed', 'Fully resolved and documented', 'IT Manager / IT Support'],
              ['Cancelled', 'Withdrawn by reporter or rejected by approver — READ ONLY', 'End User (withdraw) / Approver (reject)'],
              ['Reopened', 'Reporter reopened a resolved ticket', 'End User'],
            ]}
          />

          <p className="mt-3 font-medium">When a ticket is Cancelled:</p>
          <ul className="list-disc list-inside space-y-1 pl-2 text-xs">
            <li>The SLA timer stops immediately</li>
            <li>All pending approvals are skipped automatically</li>
            <li>The ticket becomes fully read-only — no comments, work notes, or attachments can be added</li>
            <li>A "Cancelled — Read Only" banner is shown on the detail page</li>
          </ul>

          <Tip>Resolved tickets auto-close after 72 hours (configurable per application) if the reporter does not reopen them.</Tip>
        </Section>

        {/* ── SLA ────────────────────────────────────────────────────────────── */}
        <Section id="sla" title="SLA Management" icon={Clock}>
          <p>Service Level Agreements (SLAs) define how quickly a ticket must be <strong>responded to</strong> and <strong>resolved</strong>, based on ticket priority.</p>

          <Table
            headers={['Priority', 'Example Response Time', 'Example Resolution Time']}
            rows={[
              ['P1 Critical', '15 minutes', '1 hour'],
              ['P2 High', '30 minutes', '4 hours'],
              ['P3 Medium', '1 hour', '8 hours'],
              ['P4 Low', '4 hours', '24 hours'],
            ]}
          />

          <p>Times above are examples. Actual SLA values are configured in <strong>SLA Policies</strong> (Admin menu).</p>

          <p className="mt-3 font-medium">SLA Calculation Modes:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li><strong>24/7 Calendar</strong> — SLA counts all hours (including nights and weekends)</li>
            <li><strong>Business Hours</strong> — SLA only counts Mon–Fri 9am–6pm (configurable in System Config)</li>
          </ul>

          <p className="mt-3 font-medium">SLA Timer behaviour:</p>
          <ul className="list-disc list-inside space-y-1 pl-2 text-xs">
            <li>Starts when the ticket is created</li>
            <li>Pauses during off-business hours (if Business Hours mode is active)</li>
            <li><strong>Stops permanently</strong> when ticket is Cancelled or Closed</li>
            <li>Is <strong>recalculated from the original creation time</strong> if priority changes — not from the time of change</li>
          </ul>

          <p className="mt-3 font-medium">SLA Breach:</p>
          <ul className="list-disc list-inside space-y-1 pl-2 text-xs">
            <li>A breach email is sent to the IT Manager when response or resolution time is exceeded</li>
            <li>Each breach type (response/resolution) is notified only ONCE to avoid flooding</li>
            <li>Breached tickets are highlighted in red in the SLA Breach Report</li>
          </ul>

          <Tip>Each Application can override the global SLA with its own SLA Policy Set (e.g. Payment Gateway gets stricter SLAs than an internal wiki).</Tip>
        </Section>

        {/* ── APPROVALS ──────────────────────────────────────────────────────── */}
        <Section id="approvals" title="Approval Workflow" icon={CheckCircle}>
          <p>Service Requests and Change Requests require approvals before they are assigned to IT Support. Approvals are configured per Application.</p>

          <p className="font-medium mt-2">Approval Levels:</p>
          <Table
            headers={['Level', 'Role', 'Who approves']}
            rows={[
              ['L1', 'Team Lead', 'First-line approval — team lead for the application'],
              ['L2', 'App Manager', 'Second-line — department or application manager'],
              ['L3', 'App Head', 'Final approval — division or business head'],
            ]}
          />

          <p className="font-medium mt-2">How it works:</p>
          <div className="space-y-2">
            <Step num={1} title="End User raises SR/CR">Ticket is created with status "Pending Approval". Email goes to L1 Approver.</Step>
            <Step num={2} title="L1 Approver reviews">Approver logs in → My Approvals → Approve or Reject with optional comments.</Step>
            <Step num={3} title="If approved, moves to L2 (if configured)">L2 Approver gets email. Same process.</Step>
            <Step num={4} title="Final approval triggers assignment">Ticket status changes to "Approved". If Auto-Assign is ON, ticket is automatically assigned to the next available IT Support member (round-robin). If OFF, IT Manager assigns manually.</Step>
            <Step num={5} title="Any rejection cancels the ticket">Reporter gets email with rejection reason. All pending approvals are skipped. Ticket becomes read-only.</Step>
          </div>

          <p className="mt-3 font-medium">Approval Delegation:</p>
          <p>Approvers can delegate their approval authority to another user for a specified date range (e.g., while on leave). Go to <strong>Delegations</strong> in the sidebar.</p>

          <Tip>Approvers can set up approval delegations from the Delegations page in the sidebar. Only the delegated user can approve during the delegation period.</Tip>
        </Section>

        {/* ── EMAILS ─────────────────────────────────────────────────────────── */}
        <Section id="emails" title="Email Notifications" icon={Mail}>
          <p>Emails are sent automatically based on two toggles:</p>

          <Table
            headers={['Toggle', 'Location', 'What it controls']}
            rows={[
              ['Email Notifications (master)', 'System Config → Branding', 'If OFF, zero emails are sent for this tenant regardless of other settings'],
              ['Notify Team on New Ticket', 'Applications → Edit Application', 'If ON, all team members get notified when a new ticket is raised for this app'],
            ]}
          />

          <p className="mt-3 font-medium">Email trigger matrix:</p>
          <Table
            headers={['Event', 'Who receives email', 'Ticket types']}
            rows={[
              ['Ticket Created', 'Reporter (confirmation)', 'All'],
              ['Team Notification (if app toggle ON)', 'Team members + App Owner + Technical Owner + Team Lead + App Manager', 'All'],
              ['Ticket Assigned / Reassigned', 'The newly assigned IT Support person', 'All'],
              ['Status Changed', 'Reporter (+ Assignee on Resolved/Closed)', 'All'],
              ['Approval Required', 'L1/L2/L3 Approver (in sequence)', 'SR, CR'],
              ['Approval Decision', 'Reporter', 'SR, CR'],
              ['Comment Added', 'Reporter + Assignee (not the commenter)', 'All'],
              ['SLA Breach', 'IT Manager (once per breach type)', 'All'],
              ['Escalation', 'Escalation target (L1/L2/L3 per policy)', 'All'],
              ['Distribution List (DL)', 'All of the above also CC the application DL email (if configured)', 'All'],
            ]}
          />

          <Tip>All emails include the Tenant Name in the subject and body — helpful for shared IT support teams serving multiple tenants.</Tip>
        </Section>

        {/* ── ESCALATION ─────────────────────────────────────────────────────── */}
        <Section id="escalation" title="Escalation" icon={ArrowUpFromLine}>
          <p>Escalation automatically notifies senior staff when a ticket is approaching SLA breach, ensuring critical issues get executive attention.</p>

          <p className="font-medium mt-2">How escalation works:</p>
          <div className="space-y-2">
            <Step num={1} title="Escalation Policy configured on Application">Each application can have an escalation policy with L2 / L3 / L4 thresholds (e.g. notify L2 at 75% SLA used, L3 at 90%).</Step>
            <Step num={2} title="Celery checks every minute">A background task runs every minute checking all open tickets against their SLA thresholds.</Step>
            <Step num={3} title="Escalation email sent">The appropriate escalation contact (per the policy) receives an email with full ticket details and urgency context.</Step>
            <Step num={4} title="CC emails also receive the alert">Any emails in the "Escalation CC Emails" field on the application config are also notified.</Step>
          </div>

          <Tip>Enable the Escalation toggle on the Application config page. Select an Escalation Policy. Without a policy, escalation does not trigger even if the toggle is ON.</Tip>
        </Section>

        {/* ── APPLICATION CONFIG ─────────────────────────────────────────────── */}
        <Section id="applications" title="Application Configuration" icon={Settings}>
          <p>Applications are the central configuration unit. Every ticket belongs to an Application. Go to <strong>Admin → Applications / Systems</strong> to configure.</p>

          <Table
            headers={['Setting', 'Description']}
            rows={[
              ['Application Name / Identifier', 'Display name and system code (e.g. "Payment Gateway" / "payment-gw")'],
              ['Criticality', 'Low / Medium / High / Critical — used for prioritization'],
              ['Business Owner', 'Business-side owner of the application'],
              ['Technical Owner', 'Technical contact, also receives team notifications'],
              ['L1 Team Lead', 'First-level approver for SR/CR'],
              ['L2 App Manager', 'Second-level approver'],
              ['L3 App Head', 'Third-level approver (optional)'],
              ['Auto-Assign (toggle)', 'If ON, approved SR/CR automatically assigned to IT Support (round-robin)'],
              ['Escalation CC Emails', 'Comma-separated emails CC\'d on all escalation and SLA breach alerts'],
              ['Distribution List (DL) Email', 'Single email (e.g. DL) that receives copies of ALL ticket emails for this app'],
              ['Escalation Policy + toggle', 'Which policy to use; toggle enables/disables escalation for this app'],
              ['SLA Policy Set', 'Override global SLA with application-specific timing'],
              ['Auto-Close After Resolution', 'How long (hours) before a Resolved ticket auto-closes'],
              ['SLA Calculation Mode', 'Override global mode (24/7 vs Business Hours) for this app only'],
              ['Notify Team on New Ticket (toggle)', 'Email all team members when a new ticket is raised'],
              ['Linked Categories', 'Which ticket categories are relevant to this application'],
            ]}
          />
        </Section>

        {/* ── USER MANAGEMENT ────────────────────────────────────────────────── */}
        <Section id="users" title="User Management" icon={UserCog}>
          <p>Go to <strong>Admin → User Roles</strong> to manage users.</p>

          <div className="space-y-2">
            <Step num={1} title="Create a new user">Click 'Add User'. Fill in name, email, role, department, and application assignment.</Step>
            <Step num={2} title="Assign role">Choose from Admin, IT Manager, Approver, IT Support, or End User.</Step>
            <Step num={3} title="Set password">New users get a temporary password. They can change it from their Profile page.</Step>
            <Step num={4} title="Online status">The system shows users as Online / Offline based on their last activity (active within 5 minutes = Online).</Step>
          </div>

          <p className="mt-3 font-medium">Teams (Apps & Teams Mapping):</p>
          <p>Go to <strong>Apps & Teams Mapping</strong> to create teams and link them to applications. Team members receive team notifications and appear in the Shift Calendar for that application filter.</p>

          <p className="mt-3 font-medium">Approval Delegation:</p>
          <p>Approvers can delegate their authority temporarily. Go to <strong>My Work → Delegations</strong> and specify the delegate, start date, and end date.</p>
        </Section>

        {/* ── MFA ────────────────────────────────────────────────────────────── */}
        <Section id="mfa" title="MFA & Security" icon={Lock}>
          <p>Multi-Factor Authentication (MFA) adds a second layer of security to the login process.</p>

          <Table
            headers={['Method', 'How it works']}
            rows={[
              ['Authenticator App (TOTP)', 'Use Google Authenticator, Authy, or any TOTP app. Scan the QR code from your Profile → Security tab.'],
              ['Email OTP', 'A one-time code is sent to your registered email address. Code expires in 10 minutes.'],
            ]}
          />

          <p className="font-medium mt-2">Setting up MFA:</p>
          <div className="space-y-2">
            <Step num={1} title="Go to Profile → Security tab">Click your name at the bottom of the sidebar → Profile → Security.</Step>
            <Step num={2} title="Choose method">Select Authenticator App (recommended) or Email OTP.</Step>
            <Step num={3} title="Verify">For Authenticator: scan QR code and enter the 6-digit code. For Email: request OTP and enter it.</Step>
            <Step num={4} title="MFA is now active">Future logins require both your password and the second factor.</Step>
          </div>

          <Tip>If you lose access to your authenticator app, contact your Admin to disable MFA on your account from the User Roles page.</Tip>
        </Section>

        {/* ── ATTACHMENTS ────────────────────────────────────────────────────── */}
        <Section id="attachments" title="Attachments" icon={Paperclip}>
          <p>File attachments can be added to tickets during creation and on the ticket detail page.</p>

          <Table
            headers={['Restriction', 'Default', 'Configurable']}
            rows={[
              ['Max files per ticket', '3 files', 'Yes — System Config → Attachments'],
              ['Max file size', '10 MB per file', 'Yes — System Config → Attachments'],
              ['Allowed file types', 'PDF, Word, Excel, PowerPoint, TXT, CSV, Images (JPG, PNG, etc.)', 'No'],
            ]}
          />

          <p className="mt-2 font-medium">Attachment rules:</p>
          <ul className="list-disc list-inside space-y-1 pl-2 text-xs">
            <li>The ticket detail page shows current usage: <code>Attachments (1/3)</code> with a progress bar</li>
            <li>Each file's size is shown next to the filename</li>
            <li>Upload is disabled once the limit is reached</li>
            <li>Uploads are blocked on <strong>Cancelled</strong> tickets (read-only)</li>
            <li>All attachment uploads are recorded in the ticket's Audit History</li>
          </ul>
        </Section>

        {/* ── SHIFT CALENDAR ─────────────────────────────────────────────────── */}
        <Section id="shift" title="Shift Calendar" icon={Calendar}>
          <p>The Shift Calendar manages on-call and shift assignments for IT Support staff. Go to <strong>Assignment → Shift Calendar</strong>.</p>

          <Table
            headers={['Shift', 'Hours', 'Colour']}
            rows={[
              ['Morning', '06:00 – 14:00', 'Yellow'],
              ['Day', '09:00 – 18:00', 'Blue'],
              ['Evening', '14:00 – 22:00', 'Orange'],
              ['Night', '22:00 – 06:00', 'Dark'],
              ['On Call', 'Variable', 'Green'],
            ]}
          />

          <p className="font-medium mt-2">How to use:</p>
          <div className="space-y-2">
            <Step num={1} title="Filter by Application">Select an application from the dropdown — only team members of that application are shown.</Step>
            <Step num={2} title="Navigate weeks">Use the Previous / Next week arrows to navigate. The week view shows all 7 days.</Step>
            <Step num={3} title="Assign shift">Click a cell (user + date intersection) → select a shift type or On-Call → save.</Step>
            <Step num={4} title="Remove shift">Click the shift badge on the calendar to remove the assignment.</Step>
          </div>

          <Tip>The "Users Assigned" count in the Shift Types panel shows unique users assigned to that shift in the current week — it updates dynamically as you navigate weeks.</Tip>
        </Section>

        {/* ── CMDB & KB ──────────────────────────────────────────────────────── */}
        <Section id="cmdb" title="CMDB & Knowledge Base" icon={Database}>
          <p className="font-semibold">Configuration Management Database (CMDB)</p>
          <p>The CMDB tracks all IT assets (servers, laptops, software licenses, network devices, etc.). Each asset has a type, status, owner, and can be linked to other assets to show dependencies.</p>
          <p className="text-xs text-slate-500 mt-1">Go to <strong>Resources → CMDB</strong>. IT Support and above can view. Admins can create/update assets.</p>

          <p className="font-semibold mt-4">Knowledge Base (KB)</p>
          <p>The Knowledge Base contains how-to articles, troubleshooting guides, and SOPs. All users can read. IT Staff and above can create and edit articles.</p>
          <p className="text-xs text-slate-500 mt-1">Articles are categorized by Application and Type (How-To, Troubleshooting, Reference, Policy, FAQ). Tags make search fast.</p>
        </Section>

        {/* ── SUPERADMIN ─────────────────────────────────────────────────────── */}
        <Section id="superadmin" title="Superadmin Portal" icon={Shield}>
          <p>The Superadmin Portal is accessible only to the platform administrator at <strong>/superadmin</strong>. It manages all tenants (client organizations).</p>

          <Tip>The Superadmin portal uses completely separate authentication from the regular user portal. Login: superadmin@trivont.com</Tip>

          <Table
            headers={['Feature', 'Description']}
            rows={[
              ['Tenant Management', 'Create, edit, suspend, reactivate, and delete tenant organizations'],
              ['DNS Verification', 'Before creating a tenant, verify that the subdomain DNS A record points to the server'],
              ['Automatic Provisioning', 'Creating a tenant provisions a dedicated PostgreSQL database, Nginx server block, and SSL certificate automatically'],
              ['Setup Nginx / SSL', 'Button per tenant to re-run Nginx config + certbot (use if SSL failed during initial provisioning)'],
              ['Backup Database', 'On-demand pg_dump backup of the tenant\'s database'],
              ['Usage Stats', 'Active users, ticket counts, MRR per tenant'],
              ['Audit Trail', 'Full log of all superadmin actions (tenant creation, deletion, etc.)'],
              ['Announcements', 'Broadcast announcements to all users across all tenants'],
              ['Billing', 'Track subscription plans (Free/Standard/Enterprise) and invoices'],
            ]}
          />

          <p className="font-medium mt-3">Creating a new tenant:</p>
          <div className="space-y-2">
            <Step num={1} title="Set DNS A record first">Add DNS A record: yourtenant.supportone.trivont.com → server IP. Wait for propagation.</Step>
            <Step num={2} title="Click + New Tenant">Fill in Tenant Name, Slug, Admin Email (e.g. admin@acme.com), Domain, Plan.</Step>
            <Step num={3} title="Verify DNS">Click 'Verify DNS' — must show green checkmark before provisioning is allowed.</Step>
            <Step num={4} title="Create & Provision">System automatically creates the database, runs migrations, creates admin user, configures Nginx, and obtains SSL certificate.</Step>
            <Step num={5} title="Share credentials">The success screen shows the portal URL, admin email, and default password (abc123def).</Step>
          </div>

          <Tip>Each tenant has its own isolated PostgreSQL database. Data from one tenant is never visible to another. The admin user for each tenant gets the default password abc123def — they should change it after first login.</Tip>
        </Section>

        {/* Footer */}
        <div className="mt-12 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl text-center">
          <Star className="w-5 h-5 text-amber-400 mx-auto mb-2" />
          <p className="text-sm text-slate-600 dark:text-slate-400">
            <strong>TRIVONT SupportOne</strong> — Built by TRIVONT IT Private Limited
          </p>
          <p className="text-xs text-slate-400 mt-1">For support, contact your system administrator.</p>
        </div>
      </main>
    </div>
  );
}
